<template>
  <v-container fluid>
    <page-bar :title="pageTitle" :subtitle="pageSubtitle"> </page-bar>
    <div class="px-8 py-4">
      <!-- Market Access Denied Message -->
      <v-row
        v-if="!loadingAsset && !canAccessAsset && hasMarketAccess"
        justify="center"
        class="mt-8"
      >
        <v-col cols="12" md="8" lg="6">
          <v-card class="pa-8 text-center" elevation="2">
            <v-icon size="64" color="warning" class="mb-4">mdi-lock</v-icon>
            <h2 class="mb-4 text-h5">Access Restricted</h2>
            <p class="text-body-1 mb-0">
              You cannot view this asset as it belongs to a different market
              than your currently selected one. Please switch to the appropriate
              market to access this content.
            </p>
          </v-card>
        </v-col>
      </v-row>

      <!-- Asset Content -->
      <v-row
        shrink
        dense
        class=""
        v-if="!loadingAsset && (canAccessAsset || !hasMarketAccess)"
      >
        <v-col v-if="this.editMode">
          <AssetDetailEditor
            :assetDetailid="assetDetailid"
            v-on:switchAssetDetailView="EditAsset()"
            v-on:ReloadAsset="LoadAsset()"
          ></AssetDetailEditor>
        </v-col>

        <v-col v-else>
          <AssetDetailView
            :assetDetailid="assetDetailid"
            v-on:switchAssetDetailView="EditAsset()"
            v-on:ReloadAsset="LoadAsset()"
          ></AssetDetailView>
        </v-col>

        <!--<AssetDetailEditor :assetDetailid="assetDetailid" v-on:switchAssetDetailView="EditAsset()"  v-on:ReloadAsset="LoadAsset()"></AssetDetailEditor>-->
      </v-row>
    </div>
    <Loading v-if="loadingAsset && !loadError"></Loading>

    <v-row v-if="loadError" justify="center" class="mt-8">
      <v-col cols="12" md="8" lg="6">
        <v-card class="pa-8 text-center" elevation="2">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
          <h2 class="mb-4 text-h5">Failed to Load Asset</h2>
          <p class="text-body-1 mb-4">
            Something went wrong while loading this asset. Please try again.
          </p>
          <v-btn color="primary" @click="LoadAsset()">Retry</v-btn>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";

import AssetDetailEditor from "../../views/assetDetail/AssetDetailEditor.vue";
import AssetDetailView from "../../views/assetDetail/AssetDetailView.vue";
import Loading from "../../shared/Loading";
import PageBar from "../../shared/PageBar";

export default {
  name: "AssetDetail",
  components: {
    AssetDetailEditor,
    AssetDetailView,
    Loading,
    PageBar,
  },
  props: {
    //Asset detail id
    assetDetailid: {
      default: "",
      type: String,
    },
  },
  computed: {
    ...mapState("assetxpstate", ["asset", "markets", "selectedMarketId"]),
    ...mapState("assetxpstate", ["section"]),
    ...mapGetters("assetxpstate", ["effectiveMarketId"]),
    ...mapGetters("authentication", [
      "isMarketAdmin",
      "hasMarketAccess",
      "defaultMarketId",
    ]),
    pageTitle() {
      return this.asset && this.asset.name ? this.asset.name : "Asset Details";
    },
    pageSubtitle() {
      return this.asset && this.asset.identifier
        ? `ID: ${this.asset.identifier}`
        : "";
    },
    canAccessAsset() {
      // If no asset loaded yet, allow access (loading state)
      if (!this.asset) return true;

      // If asset has no market metadata, do not block in UI.
      if (!this.asset.marketId) return true;

      const assetMarketId = String(this.asset.marketId).toLowerCase();
      const resolvedMarketIds = Array.isArray(this.markets)
        ? this.markets
            .map((m) => (m && m.id ? String(m.id).toLowerCase() : null))
            .filter(Boolean)
        : [];

      // Hard deny only when we have a resolved market list and the asset market is not in it.
      if (
        resolvedMarketIds.length > 0 &&
        !resolvedMarketIds.includes(assetMarketId)
      ) {
        return false;
      }

      const activeMarketCandidates = [
        this.effectiveMarketId,
        this.selectedMarketId,
        this.defaultMarketId,
      ]
        .filter(Boolean)
        .map((id) => String(id).toLowerCase());

      // If market context is still settling, don't show a false restricted state.
      if (activeMarketCandidates.length === 0) return true;

      if (activeMarketCandidates.includes(assetMarketId)) return true;

      // Enforce active market mismatch only when market context is fully resolved.
      return resolvedMarketIds.length === 0;
    },
  },
  mounted: function() {
    this.LoadAsset();
  },
  data: () => ({
    editMode: false,
    overlay: false,
    loadingAsset: true,
    loadError: false,
    actions: [
      {
        description: "Edit Asset",
        icon: "mdi-pencil",
        onClick: "EditAsset",
      },
      {
        description: "Delete Asset",
        icon: "mdi-delete",
        onClick: "DeleteAsset",
      },
    ],
  }),
  methods: {
    ...mapGetters("authentication", ["CanUserEditAssets"]),
    ...mapActions("assetxpstate", [
      "RemoveAsset",
      "GetAsset",
      "UpdateAsset",
      "GetSectionById",
      "UpdateAssetAttributeMulti",
      "UpdateAssetAttribute",
      "UpdateAssetAttributeAsset",
      "AddAttributeToAsset",
      "RemoveAttributeFromAsset",
      "GetClonableSectionIds",
    ]),
    ...mapActions("navstate", ["GetSelectedSection"]),

    LoadAsset() {
      this.loadingAsset = true;
      this.loadError = false;
      this.GetAsset(this.assetDetailid).then(() => {
        console.log("[MarketTrace][AssetDetail] loaded asset", {
          assetId: this.asset && this.asset.id,
          assetMarketId: this.asset && this.asset.marketId,
          selectedMarketId: this.$store.state.assetxpstate.selectedMarketId,
          effectiveMarketId: this.effectiveMarketId,
          canAccessAsset: this.canAccessAsset,
        });
        if (!this.asset || !this.asset.sectionid) {
          console.error("[AssetDetail] Asset loaded but missing sectionid", this.asset);
          this.loadingAsset = false;
          this.loadError = true;
          return;
        }
        this.GetSectionById(this.asset.sectionid).then(() => {
          this.loadingAsset = false;
          this.editMode = true;
          this.GetSelectedSection(this.section.name);
          this.GetClonableSectionIds();
        }).catch((error) => {
          console.error("[AssetDetail] Failed to load section", error);
          this.loadingAsset = false;
          this.loadError = true;
        });
      }).catch((error) => {
        console.error("[AssetDetail] Failed to load asset", error);
        this.loadingAsset = false;
        this.loadError = true;
      });
    },
    HandleFunctionCall(functionName) {
      this[functionName]();
    },
    EditAsset() {
      this.editMode = !this.editMode;
    },
  },
  watch: {
    $route() {
      this.LoadAsset();
    },
  },
};
</script>

<style scoped>
.v-btn {
  width: 30px;
  height: 30px;
  margin: 0px;
  padding: 0px;
}
.v-icon:hover {
  color: blue;
}
.cardContent {
  margin: 0px;
  padding: 5px;
}
</style>
